/**
 * Phase 20 — one auto-send layer read back in three lines: sending, daily
 * cap, stop-loss. A campaign group's (its master config) or a container's
 * own layer, whose switch can only take it out and whose stop-loss is
 * optional (the group's rule still judges it).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { Gauge, Send, ShieldAlert } from 'lucide-react';
import { type DropGenAutoSendConfig, stopLossOn } from '@trafficpulse/shared';

import { describeStopLoss, type LayerKind } from '../../board-header/autosend/settings/autosend-draft';

const Row: FC<{ icon: typeof Send; label: string; children: ReactNode }> = ({ icon: Icon, label, children }) => (
  <li className="flex items-start gap-2">
    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
    <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
    <span className="min-w-0">{children}</span>
  </li>
);

const sending = (c: DropGenAutoSendConfig, kind: LayerKind, groupName: string) => {
  if (c.autoSend && c.shadow) return 'simulated — the engine says when it would have sent each draft and sends nothing; you still send';
  if (kind === 'group') return c.autoSend ? 'on — the engine sends every container’s drafts at their time' : 'off — you send';
  return c.autoSend ? `on while ${groupName} is on` : 'off — this container waits for a person, the rest of the group still sends';
};

const stopLoss = (c: DropGenAutoSendConfig, kind: LayerKind, groupName: string) =>
  kind === 'container' && !stopLossOn(c) ? `none of its own — ${groupName}’s rule judges the whole group` : describeStopLoss(c);

export const LayerSummary: FC<{
  title: string;
  config: DropGenAutoSendConfig;
  kind: LayerKind;
  groupName: string;
}> = ({ title, config, kind, groupName }) => (
  <div className="flex flex-col gap-1.5 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 text-xs">
    <p className="font-semibold">{title}</p>
    <ul className="flex flex-col gap-1">
      <Row icon={Send} label="Sending">{sending(config, kind, groupName)}</Row>
      <Row icon={Gauge} label="Daily cap">
        {config.dailySmsCap === null ? (kind === 'group' ? 'no limit' : `none of its own — ${groupName}’s applies`) : `${config.dailySmsCap.toLocaleString('en-US')} SMS a day`}
      </Row>
      <Row icon={ShieldAlert} label="Stop-loss">{stopLoss(config, kind, groupName)}</Row>
    </ul>
  </div>
);
